"use client"

import type React from "react"
import { useEffect, useState, useRef, useMemo, useCallback } from "react"
import Image from "next/image"
import dynamic from "next/dynamic"
import styles from "./page.module.css"

// Lazy load react-pageflip - chỉ load khi cần
const HTMLFlipBook = dynamic(() => import("react-pageflip"), {
  loading: () => null,
  ssr: false,
});

const FlipBook = HTMLFlipBook as any

const teamImages = [
  "/assets/sdtc-image/ve-chung-toi/user1.webp",
  "/assets/sdtc-image/ve-chung-toi/user2.webp",
  "/assets/sdtc-image/ve-chung-toi/user3.webp",
  "/assets/sdtc-image/ve-chung-toi/user4.webp",
  "/assets/sdtc-image/ve-chung-toi/user5.png",
  "/assets/sdtc-image/ve-chung-toi/image1.webp",
  "/assets/sdtc-image/ve-chung-toi/image2.webp",
  "/assets/sdtc-image/ve-chung-toi/image4.webp",
  "/assets/sdtc-image/ve-chung-toi/image5.webp",
  "/assets/sdtc-image/ve-chung-toi/image6.webp",
]

const devTeams = [
  {
    image: 6,
    desc: [
      "Chúng tôi là tập thể trẻ, năng động, sáng tạo, có mặt tại TP.HCM. Dưới sự dẫn dắt của anh Nguyễn Đức Nghĩa (CEO), đội ngũ phát triển được trao quyền để học hỏi, sáng tạo và làm việc với công nghệ mới nhất.",
      "Chúng tôi áp dụng quy trình Agile, CI/CD tự động và văn hóa làm việc mở — nơi mọi ý tưởng đều được lắng nghe.",
    ],
  },
  {
    image: 7,
    desc: [
      "Đội ngũ Frontend luôn đặt trải nghiệm người dùng làm trung tâm, sử dụng các công nghệ mới nhất như Vue, React và TailwindCSS.",
      "Chúng tôi tạo nên giao diện tinh gọn, hiệu suất cao và tương thích đa nền tảng.",
    ],
  },
  {
    image: 8,
    desc: [
      "Đội ngũ Backend tập trung vào hiệu năng, bảo mật và khả năng mở rộng của hệ thống.",
      "Chúng tôi xây dựng kiến trúc microservices, API RESTful và tối ưu cơ sở dữ liệu MongoDB & PostgreSQL.",
    ],
  },
  {
    image: 9,
    desc: [
      "Đội ngũ UX/UI nghiên cứu hành vi người dùng để thiết kế sản phẩm trực quan và tinh tế.",
      "Mỗi chi tiết đều được cân nhắc kỹ lưỡng nhằm mang lại trải nghiệm tốt nhất.",
    ],
  },
]

const leaders = [
  {
    name: "NGUYỄN ĐỨC NGHĨA",
    role: "GIÁM ĐỐC ĐIỀU HÀNH",
    bio: "Định hướng chiến lược phát triển doanh nghiệp, kết nối con người và công nghệ để tạo nên giá trị bền vững.",
    image: 0,
  },
  {
    name: "TRẦN HẢI VINH",
    role: "TRƯỞNG PHÒNG MARKETING",
    bio: "Xây dựng và triển khai chiến lược truyền thông, định vị thương hiệu qua các nền tảng số.",
    image: 1,
  },
  {
    name: "PHẠM THANH NHÃ",
    role: "TRƯỞNG NHÓM SÁNG TẠO NỘI DUNG",
    bio: "Dẫn dắt đội ngũ bán hàng, mở rộng thị trường và duy trì mối quan hệ hợp tác chiến lược với khách hàng.",
    image: 2,
  },
  // {
  //   name: "VÕ NGỌC HÀ NHI",
  //   role: "TRƯỞNG PHÒNG KINH DOANH",
  //   bio: "Dẫn dắt đội ngũ bán hàng, mở rộng thị trường và duy trì mối quan hệ hợp tác chiến lược với khách hàng.",
  //   image: 3,
  // },
  { name: "DIỆP TRÍ THÀNH",
    role: "TRƯỞNG NHÓM PHÁT TRIỂN HỆ THỐNG",
    bio: "Chịu trách nhiệm phát triển kiến trúc phần mềm, tối ưu hiệu năng và bảo mật toàn hệ thống.",
    image: 4,
  },
]

export default function DoiNguPage() {
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const [isMuted, setIsMuted] = useState(true) // mặc định tắt âm thanh
  const [isMobile, setIsMobile] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const flipBookRef = useRef<any>(null)
  const autoPlayTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const bookSectionRef = useRef<HTMLElement | null>(null)

  const totalContentPages = useMemo(() => devTeams.length * 2, [])
  const totalPages = useMemo(() => totalContentPages + 2, [totalContentPages])

  // ===== XỬ LÝ VIDEO: TỰ ĐỘNG PHÁT + TẮT ÂM THANH KHI VÀO TRANG, PAUSE KHI RỜI TRANG =====
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Tắt âm thanh + play ngay khi vào trang
    video.muted = true
    setIsMuted(true)

    const playVideo = () => {
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video phát thành công
          })
          .catch(() => {
            // Autoplay bị chặn → thử lại
            video.play().catch(() => {})
          })
      }
    }

    // Delay nhỏ để tránh block main thread
    const timeoutId = setTimeout(playVideo, 100)

    // Khi rời khỏi trang → pause video
    return () => {
      clearTimeout(timeoutId)
      video.pause()
    }
  }, [])

  const toggleSound = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }, [])

  const handleVideoLoaded = useCallback(() => {
    setVideoLoaded(true)
  }, [])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    const checkMobile = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 1024)
      }, 150) // Debounce resize
    }
    
    setIsMobile(window.innerWidth < 1024)
    window.addEventListener("resize", checkMobile, { passive: true })
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  useEffect(() => {
    const element = bookSectionRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.bookVisible)
            // Disconnect sau khi đã visible để tránh re-check
            observer.unobserve(entry.target)
          }
        })
      },
      { 
        threshold: 0.05, 
        rootMargin: "0px 0px -5% 0px",
      },
    )
    
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Lazy load video khi scroll đến gần video section
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && video.readyState === 0) {
            video.load()
            observer.unobserve(entry.target)
          }
        })
      },
      { rootMargin: "200px" } // Load video khi còn cách 200px
    )

    observer.observe(video)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (hasOpened || isMobile) return

    // Đợi cho đến khi flipBook sẵn sàng
    const tryOpenBook = () => {
      if (!flipBookRef.current) {
        requestAnimationFrame(tryOpenBook)
        return
      }

      const pageFlip = flipBookRef.current.pageFlip()
      if (!pageFlip) {
        requestAnimationFrame(tryOpenBook)
        return
      }

      // Delay 0.5s trước khi mở sách, sử dụng requestAnimationFrame
      setTimeout(() => {
        requestAnimationFrame(() => {
          pageFlip.flip(1)
          setHasOpened(true)
          setIsAutoPlay(true)
        })
      }, 500)
    }

    // Bắt đầu thử mở ngay
    requestAnimationFrame(tryOpenBook)
  }, [hasOpened, isMobile])

  useEffect(() => {
    if (!isAutoPlay || !flipBookRef.current) return

    const book = flipBookRef.current.pageFlip()
    if (!book) return
    
    let nextPage = currentPageIndex + 1

    if (currentPageIndex === totalPages - 1) return

    // Sử dụng requestAnimationFrame để tối ưu timing
    autoPlayTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        book.flip(nextPage)
      })
    }, 3500)

    return () => {
      if (autoPlayTimeoutRef.current) {
        clearTimeout(autoPlayTimeoutRef.current)
        autoPlayTimeoutRef.current = null
      }
    }
  }, [currentPageIndex, isAutoPlay, totalPages])

  const handleFlip = useCallback((e: any) => {
    const page = e.data
    setCurrentPageIndex(page)

    if (page === 0) {
      setCurrentTeamIndex(0)
    } else if (page === totalPages - 1) {
      setCurrentTeamIndex(devTeams.length - 1)
    } else {
      const teamIndex = Math.floor((page - 1) / 2)
      setCurrentTeamIndex(Math.min(teamIndex, devTeams.length - 1))
    }
  }, [totalPages])

  const goToPage = useCallback((teamIdx: number) => {
    if (!flipBookRef.current) return
    setIsAutoPlay(false)
    const targetPage = teamIdx * 2 + 1
    flipBookRef.current.pageFlip()?.flip(targetPage)
    setTimeout(() => setIsAutoPlay(true), 10000)
  }, [])

  const handleMouseEnter = useCallback(() => setIsAutoPlay(false), [])
  const handleMouseLeave = useCallback(() => setIsAutoPlay(true), [])

  return (
    <main className={styles.page}>
      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            {"GẶP GỠ ĐỘI NGŨ CỦA CHÚNG TÔI".split("").map((char, i) => (
              <span key={i} className={styles.heroLetter} style={{ "--i": i } as React.CSSProperties}>
                {char === " " ? "\u00A0" : char}
              </span>
            ))}
          </h1>
        </div>
      </section>

      {/* HERO TEXT */}
      <section className={styles.heroText}>
        <div className={styles.container}>
          <p className={styles.heroDesc}>
            NHỮNG CON NGƯỜI TRÀN ĐẦY NHIỆT HUYẾT, SÁNG TẠO KHÔNG NGỪNG, KHÁT VỌNG KIẾN TẠO TƯƠNG LAI CÔNG NGHỆ VIỆT NAM
            — NƠI MỖI CÁ NHÂN LÀ NGUỒN CẢM HỨNG VÀ MỖI Ý TƯỞNG LÀ MỘT BƯỚC TIẾN ĐỂ THAY ĐỔI THẾ GIỚI.
          </p>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className={styles.leadership}>
        <div className={styles.container}>
          <div className={styles.leadershipHeader}>
            <h2 className={styles.sectionTitle}>Ban lãnh đạo</h2>
          </div>

          <div className={styles.leaderSlider}>
            <div className={styles.leaderRow}>
              {leaders.map((leader, idx) => (
                <article key={leader.name} className={`${styles.teamCard} ${styles.cardVisible}`} style={{ "--card-index": idx } as React.CSSProperties}>
                  <div className={styles.cardImgWrapper}>
                    <Image
                      src={teamImages[leader.image] || "/placeholder.svg"}
                      alt={leader.name}
                      width={400}
                      height={400}
                      className={styles.cardImg}
                      priority={idx < 3}
                      fetchPriority={idx < 3 ? "high" : "auto"}
                      loading={idx < 3 ? "eager" : "lazy"}
                      quality={60}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    <div className={styles.cardOverlay}>
                      <p>{leader.role}</p>
                    </div>
                  </div>
                  <div className={styles.cardContent}>
                    <h3 className={styles.cardName}>{leader.name}</h3>
                    <p className={styles.cardBio}>{leader.bio}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BOOK SECTION */}
      <section className={styles.bookSection} ref={bookSectionRef}>
        <div className={styles.container}>
          <div className={styles.leadershipHeader}>
            <h2 className={styles.sectionTitle}>Đội ngũ phát triển</h2>
          </div>
          <div className={styles.bookContainer}>
            <div
              className={`${styles.flipBookWrapper} ${hasOpened ? styles.opened : ""} ${
                currentPageIndex === 0 ? styles.coverCenteredFirst : ""
              } ${
                currentPageIndex === totalPages - 1 ? styles.coverCenteredLast : ""
              }`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {HTMLFlipBook && (
                <FlipBook
                  width={700}
                  height={840}
                  size="stretch"
                  minWidth={isMobile ? 280 : 360}
                  maxWidth={isMobile ? 420 : 1200}
                  minHeight={isMobile ? 420 : 480}
                  maxHeight={1000}
                  maxShadowOpacity={0.6}
                  showCover={true}
                  mobileScrollSupport={true}
                  usePortrait={isMobile}
                  startPage={0}
                  drawShadow={true}
                  flippingTime={1200}
                  className={styles.flipBook}
                  ref={flipBookRef}
                  onFlip={handleFlip}
                >
                {/* BÌA TRƯỚC */}
                <div className={styles.coverPage} data-density="hard">
                  <div className={styles.coverInner}>
                    <Image 
                      src="/assets/sdtc-image/ve-chung-toi/bia.png" 
                      alt="Cover" 
                      width={700}
                      height={840}
                      className={styles.coverPageImg}
                      priority
                      quality={60}
                      loading="eager"
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//9k="
                    />
                  </div>
                </div>

                {/* NỘI DUNG */}
                {devTeams.flatMap((team, index) => [
                  <div key={`img-${index}`} className={styles.demoPage}>
                    <div className={styles.pageInner}>
                      <Image
                        src={teamImages[team.image] || "/placeholder.svg"}
                        alt={`Team ${index + 1}`}
                        width={600}
                        height={500}
                        className={styles.pageImg}
                        loading={index < 2 ? "eager" : "lazy"}
                        quality={60}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                      />
                    </div>
                  </div>,
                  <div key={`text-${index}`} className={styles.demoPage}>
                    <div className={styles.pageInner}>
                      <div className={styles.pageContent}>
                        {team.desc.map((p, i) => (
                          <p key={i} className={styles.paragraph}>
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>,
                ])}

                {/* BÌA SAU */}
                <div className={`${styles.coverPageBack} ${currentPageIndex === totalPages - 1 ? styles.lastPageActive : ""}`} data-density="hard">
                  <div className={styles.coverBackInner}>
                    <div className={styles.thankYouContent}>
                      <div className={styles.thankYouIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <h3 className={styles.thankYouTitle}>Cảm ơn bạn đã xem!</h3>
                      <p className={styles.thankYouText}>Chúng tôi luôn sẵn sàng đồng hành cùng bạn</p>
                      <div className={styles.sparkles}>
                      <span className={styles.sparkle} style={{ "--delay": "0s" } as React.CSSProperties}>✦</span>
                      <span className={styles.sparkle} style={{ "--delay": "0.3s" } as React.CSSProperties}>✦</span>
                      <span className={styles.sparkle} style={{ "--delay": "0.6s" } as React.CSSProperties}>✦</span>
                      <span className={styles.sparkle} style={{ "--delay": "0.9s" } as React.CSSProperties}>✦</span>
                      <span className={styles.sparkle} style={{ "--delay": "1.2s" } as React.CSSProperties}>✦</span>
                    </div>
                    </div>
                  </div>
                </div>
              </FlipBook>
              )}
            </div>
          </div>

          {/* DOTS */}
          <div className={styles.dots}>
            {devTeams.map((_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                className={`${styles.dot} ${currentTeamIndex === i ? styles.activeDot : ""}`}
                aria-label={`Go to team ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO SECTION - ĐÃ HOÀN CHỈNH */}
      <section className={styles.videoSection}>
        <div className={styles.container}>
          <div className={styles.videoHeader}>
            <h2 className={styles.videoTitle}>VINH DỰ</h2>
            <p className={styles.videoSubtitle}>
              Những khoảnh khắc đáng nhớ, những thành tựu chúng tôi tự hào và hành trình đồng hành cùng khách hàng – tất
              cả được ghi lại trong video dưới đây.
            </p>
          </div>

          <div className={styles.videoWrapper}>
            {!videoLoaded && (
              <div className={styles.videoLoading}>
                <div className={styles.spinner}></div>
                <p>Đang tải video...</p>
              </div>
            )}
            <video
              ref={videoRef}
              className={styles.videoPlayer}
              autoPlay
              loop
              playsInline
              preload="none"
              poster="/video/banner.webp"
              onLoadedData={handleVideoLoaded}
              style={{ opacity: videoLoaded ? 1 : 0, transition: "opacity 0.6s ease" }}
            >
              <source src="/video/vinhdu.mp4" type="video/mp4" />
              Trình duyệt của bạn không hỗ trợ video.
            </video>

            {videoLoaded && (
              <button
                onClick={toggleSound}
                className={styles.soundToggle}
                aria-label={isMuted ? "Bật âm thanh" : "Tắt âm thanh"}
              >
                {isMuted ? (
                  <svg className={styles.soundOff} viewBox="0 0 24 24" width="20" height="20">
                    <path
                      fill="white"
                      d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                    />
                  </svg>
                ) : (
                  <svg className={styles.soundOn} viewBox="0 0 24 24" width="20" height="20">
                    <path
                      fill="white"
                      d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </section>
    </main>
  )
} 